import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  Alert,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { api } from "../../utils/api";

const ROLES = ["Admin", "BDM", "Manager", "Team", "Auditor"];
const GENDERS = ["Male", "Female", "Other"];

export default function UserForm({ data, onSuccess, onClose }) {
  const isEdit = !!data;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    address: "",
    city: "",
    pincode: "",
    state: "",
    gender: "",
    employee_code: "",
    is_active: 1,
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit && data) {
      setForm({
        name: data.name || "",
        email: data.email || "",
        phone: data.phone || "",
        role: data.role || "",
        address: data.address || "",
        city: data.city || "",
        pincode: data.pincode || "",
        state: data.state || "",
        gender: data.gender || "",
        employee_code: data.employee_code || "",
        is_active: data.is_active ?? 1,
        password: "",
      });
    }
  }, [data, isEdit]);

  const setField = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email";
    if (!form.role) e.role = "Required";
    if (!isEdit && !form.password) e.password = "Password required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (isEdit) {
        payload.id = data.id;
        if (!payload.password) delete payload.password;
        await api.post("/users/update.php", payload);
      } else {
        await api.post("/users/create.php", payload);
      }
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (err) {
      setSubmitError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Full Name *"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email *"
            type="email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone"
            inputProps={{ maxLength: 10 }}
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Gender"
            value={form.gender}
            onChange={(e) => setField("gender", e.target.value)}
          >
            <MenuItem value="">Select</MenuItem>
            {GENDERS.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Role *"
            value={form.role}
            onChange={(e) => setField("role", e.target.value)}
            error={!!errors.role}
            helperText={errors.role}
          >
            <MenuItem value="">Select role</MenuItem>
            {ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Employee Code"
            value={form.employee_code}
            onChange={(e) => setField("employee_code", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Status"
            value={form.is_active}
            onChange={(e) => setField("is_active", e.target.value)}
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="password"
            label={isEdit ? "New Password (optional)" : "Password *"}
            value={form.password}
            onChange={(e) => setField("password", e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Address"
            value={form.address}
            onChange={(e) => setField("address", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="City"
            value={form.city}
            onChange={(e) => setField("city", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="State"
            value={form.state}
            onChange={(e) => setField("state", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Pincode"
            value={form.pincode}
            onChange={(e) => setField("pincode", e.target.value)}
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: "flex-end" }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
        >
          {saving ? "Saving…" : isEdit ? "Update User" : "Create User"}
        </Button>
      </Stack>
    </Box>
  );
}
